import type { SmartSort } from '@iheartjane/dm-sdk';
import { Placement } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardCarousel, MagicRowSubtitle, resolveRowId } from '@iheartjane/menu';
import { EventNames } from '@iheartjane/tracking';

import { useSearchContext } from '@jane/search/hooks';
import type { AlgoliaProduct } from '@jane/search/types';
import { MenuProductCarousel } from '@jane/shared-ecomm/components';
import type { MenuProductCarouselVariant } from '@jane/shared-ecomm/components';
import { useMenuRowWeightFilter } from '@jane/shared-ecomm/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';
import type { EcommPaths } from '@jane/shared/types';
import { getAppliedWeightFilter } from '@jane/shared/util';

import { MenuProductCard } from '../../MenuComponents/MenuProductCards/MenuProductCard/MenuProductCard';

interface MenuCarouselProps {
  disableWeightFilter?: boolean;
  index: number;
  placement: SmartSort<AlgoliaProduct>;
}

const determineVariant = (
  placement: Placement | undefined
): MenuProductCarouselVariant => {
  switch (placement) {
    case Placement.MenuDynamicRow:
      return 'magic-row';
    case Placement.MenuTopRow:
      return 'sponsored';
    default:
      return 'default';
  }
};

const determineViewAllTo = (
  placement: Placement | undefined,
  title: string | undefined,
  rowId: number | undefined,
  paths: EcommPaths
): string | undefined => {
  switch (placement) {
    case Placement.MenuDynamicRow:
    case Placement.MenuTopRow:
      return undefined;
    default:
      if (rowId && title) {
        return paths.menuRow({ id: rowId, title });
      }
      if (title) {
        return paths.menuFeaturedCategory(title);
      }
      return undefined;
  }
};

export const MenuCarousel = (props: MenuCarouselProps) => {
  const thematicRowContextFlag = useFlag(FLAGS.c3ThematicRowsContext);
  const { disableWeightFilter, placement, index } = props;
  const { paths, listView, store } = useMenu();
  const { searchState } = useSearchContext();

  const customRowSettings = store.custom_row_settings;
  const weightFilter = useMenuRowWeightFilter({
    customRowSettings,
    rowId: placement.rowId,
  });

  if (placement.products.length === 0) {
    return null;
  }

  const nextBreadcrumbs = [
    {
      title: 'Featured',
      url: paths.menuCategory('featured'),
    },
  ];

  const variant = determineVariant(placement.placement);

  const resolvedRowId = resolveRowId(
    placement.rowId,
    placement.title,
    customRowSettings
  );

  const viewAllTo = determineViewAllTo(
    placement.placement,
    placement.title,
    resolvedRowId,
    paths
  );

  const allowViewAll =
    placement.placement !== Placement.MenuDynamicRow &&
    placement.placement !== Placement.MenuTopRow;

  const onViewAllClick = allowViewAll
    ? () => {
        track({
          category: placement.title || '',
          event: EventNames.ClickedSeeAll,
          linkLocation: 'see all link',
          numberOfItems: placement.totalProducts,
          placementIndex: index,
          storeId: `${store.id}`,
        });
      }
    : undefined;

  return (
    <MenuProductCarousel
      buttonAriaLabel={
        allowViewAll && placement.title
          ? `View all ${placement.title} products`
          : undefined
      }
      name={placement.title || ''}
      listView={listView}
      mode="ideal-width"
      subtitle={
        thematicRowContextFlag &&
        placement.placement === Placement.MenuDynamicRow ? (
          <MagicRowSubtitle />
        ) : undefined
      }
      variant={variant}
      viewAllTo={viewAllTo}
      viewAllOnClick={onViewAllClick}
    >
      {placement.products.map((product) => {
        const productKey = `${product?.isSponsored ? 'inline-ad-' : 'mp-'}${
          product.objectId
        }`;

        return (
          <CardCarousel.Card key={productKey} width="100%" widthMobile="100%">
            <MenuProductCard
              appliedWeightFilter={getAppliedWeightFilter(searchState)}
              algoliaIndexName={''}
              breadcrumbs={nextBreadcrumbs}
              columnPosition={index}
              disableWeightFilter={disableWeightFilter}
              menuRowName={placement.title || ''}
              product={product}
              productLocation={`featured/${placement.title}`}
              {...(weightFilter && { weightFilter })}
            />
          </CardCarousel.Card>
        );
      })}
    </MenuProductCarousel>
  );
};
